import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    Box,
    Chip,
    IconButton,
    Slider,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCalculator } from '../../../../contexts/CalculatorContext';
import { getEffectiveChatUsers } from '../../../../utils/tierUsers';
import OmnichannelChatSettingsDialog from '../../modals/OmnichannelChatSettingsDialog';

/**
 * OmnichannelChat component for the pricing calculator
 * @param {Object} props - Component props
 * @param {Object} props.product - Product data (value, min, max, etc.)
 * @param {Function} props.toggleProduct - Function to toggle product selection
 */
const OmnichannelChat = ({ product, toggleProduct }) => {
  const { t } = useTranslation();
  const { veryCustomProductDiscounts, businessPhoneUsers, businessPhoneType, packageTier, isProductSelected } = useCalculator();

  // Check if this product is included in the current tier (Pro/Corporate)
  const isIncludedInTier = packageTier === 'pro' || packageTier === 'corporate';

  // For Basic/Advanced tier, chat users = Business Phone users (per-user pricing, locked)
  const isBasicTier = packageTier === 'basic' || packageTier === 'advanced';

  // Chat users follow Business Phone users only — FMC users have no smartphone
  // app and can never be chat users.
  const effectiveChatUsers = isBasicTier
    ? getEffectiveChatUsers({ businessPhoneUsers, businessPhoneType, packageTier })
    : product.value;

  // Settings dialog state
  const [settingsOpen, setSettingsOpen] = React.useState(false);

  // Check if there are any custom settings (including very custom discounts)
  const hasAnyCustomSettings = veryCustomProductDiscounts['omnichannel_chat'] && veryCustomProductDiscounts['omnichannel_chat'] > 0;

  // Local state for inputs to allow temporary empty strings
  const [usersInput, setUsersInput] = React.useState(product.value);

  // Legacy mode: Effective max for non-package mode
  const allowedMax = businessPhoneType === 'fmc' ? 0 : (Number(businessPhoneUsers) || 0);
  const canSetUsers = !isBasicTier && allowedMax >= product.min;
  const sliderMax = 100; // UI convenience: 99% кейсів до 100 користувачів

  // Sync local state if the prop values change from elsewhere
  React.useEffect(() => {
    if (product.value !== usersInput) {
      setUsersInput(product.value);
    }
  }, [product.value]);

  // Handle slider change with debouncing
  const handleSliderChange = (_, newValue) => {
    setUsersInput(newValue); // Update input immediately for visual feedback
    // Only update the actual value to prevent excessive saves
    const v = canSetUsers ? Math.min(Number(newValue), allowedMax) : Number(newValue);
    product.setValue(v);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    // Allow empty string and valid numbers
    if (value === '' || /^\d+$/.test(value)) {
      setUsersInput(value);
      // Do not update context here to allow free typing; apply on blur
    }
  };

  // Handle input blur
  const handleInputBlur = () => {
    if (usersInput === '' || parseInt(usersInput, 10) < product.min) {
      const minValue = product.min;
      product.setValue(minValue);
      setUsersInput(minValue);
    } else {
      const parsed = parseInt(usersInput, 10);
      const v = canSetUsers ? Math.max(product.min, Math.min(parsed, allowedMax)) : parsed;
      product.setValue(v);
      setUsersInput(v);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChatBubbleOutlineIcon sx={{ color: '#333', fontSize: 20 }} />
          <Typography sx={{ fontWeight: 500 }}>
            {t('calculator.omnichannelChat', 'Omnichannel Chat')}
          </Typography>
          {isIncludedInTier && (
            <Chip
              label={t('calculator.included', 'INCLUDED')}
              size="small"
              sx={{
                bgcolor: 'rgba(0, 200, 83, 0.1)',
                color: '#00C853',
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 20,
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Hide settings and close button when included in tier */}
          {!isIncludedInTier && (
            <>
              <Tooltip title={t('calculator.omnichannelChatSettings', 'Omnichannel Chat Settings')}>
                <IconButton
                  size="small"
                  onClick={() => setSettingsOpen(true)}
                  sx={{
                    color: hasAnyCustomSettings ? '#00C853' : '#757575',
                    mr: 1,
                    '&:hover': { color: '#00C853' }
                  }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => toggleProduct(product.id)}
                sx={{
                  '&:hover': {
                    bgcolor: 'rgba(255, 0, 0, 0.04)'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </Box>


      {/* Controls - hide when included in tier (Pro/Corporate) */}
      {!isIncludedInTier && (
        <>
          {/* For Basic tier - show locked users count tied to BP/FMC */}
          {isBasicTier ? (
            <>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2, mb: 0.5 }}>
                {t('calculator.chatUsersLabel', 'Number of users')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Slider
                    value={effectiveChatUsers}
                    min={3}
                    max={100}
                    disabled
                    sx={{
                      color: '#00C853',
                      '& .MuiSlider-thumb': {
                        width: 16,
                        height: 16,
                        backgroundColor: '#00C853',
                      }
                    }}
                  />
                </Box>
                <TextField
                  value={effectiveChatUsers}
                  disabled
                  type="number"
                  size="small"
                  sx={{ width: 90 }}
                  inputProps={{ inputMode: 'numeric' }}
                />
              </Box>
              {/* Info message explaining the locked state */}
              <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">
                  {t('calculator.chatUsersLinkedInfo', 'Chat users are linked to Business Phone users count (FMC users cannot use chat)')}
                </Typography>
              </Box>
            </>
          ) : (
            /* Legacy mode - editable slider (when no package tier) */
            <>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2, mb: 0.5 }}>
                {t('calculator.chatUsersLabel', 'Number of users')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Slider
                    value={typeof usersInput === 'number' ? usersInput : product.value}
                    onChange={(_, v) => {
                      const clamped = canSetUsers ? Math.min(Number(v), allowedMax) : Number(v);
                      setUsersInput(clamped);
                    }}
                    onChangeCommitted={(_, v) => {
                      const value = Number(v);
                      const newVal = canSetUsers ? Math.min(value, allowedMax) : value;
                      setUsersInput(newVal);
                      product.setValue(newVal);
                    }}
                    min={product.min}
                    max={sliderMax}
                    disabled={!canSetUsers}
                    sx={{
                      color: '#00C853',
                      '& .MuiSlider-thumb': {
                        width: 16,
                        height: 16,
                        backgroundColor: '#00C853',
                      }
                    }}
                  />
                </Box>
                <TextField
                  value={usersInput}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  type="number"
                  size="small"
                  disabled={!canSetUsers}
                  sx={{
                    width: 90,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                      '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.87)' },
                      '&.Mui-focused fieldset': { borderColor: '#00C853' },
                    },
                  }}
                  inputProps={{ min: product.min, style: { textAlign: 'left' } }}
                />
              </Box>
              {/* Helper message when editing is blocked or limited */}
              {(!canSetUsers || allowedMax < sliderMax) && (
                <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption">
                    {(!canSetUsers && businessPhoneType === 'fmc')
                      ? t('calculator.omnichannelChatFmcInfo', 'Omnichannel Chat доступний лише з Business Phone. При FMC редагування недоступне.')
                      : t('calculator.omnichannelChatLimitInfoWithValue', { defaultValue: 'Максимум для чату — {{max}} (дорівнює кількості користувачів Business Phone). Щоб вказати більше в інпуті, спочатку збільште Business Phone.', max: allowedMax })}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Show included message for Pro/Corporate tiers */}
      {isIncludedInTier && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          {t('calculator.includedInTier', 'Included in your {{tier}} plan', { tier: packageTier.charAt(0).toUpperCase() + packageTier.slice(1) })}
        </Typography>
      )}
      
      {/* Settings Dialog */}
      <OmnichannelChatSettingsDialog 
        open={settingsOpen} 
        onClose={() => setSettingsOpen(false)} 
      />
    </Box>
  );
};

export default OmnichannelChat;